import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Reveal from '../components/Reveal';
import ProductCard from '../components/ProductCard';
import supabase from '../supabase/supabase';

const {
  FiArrowLeft, FiShoppingBag, FiChevronLeft, FiChevronRight,
  FiTruck, FiShield, FiRefreshCw, FiStar, FiPlay, FiAlertCircle,
  FiVolumeX, FiVolume2
} = FiIcons;

const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i;

function isDirectVideoUrl(url) {
  if (!url) return false;
  return VIDEO_EXTENSIONS.test(url);
}

function ProductDetail({ onAdd }) {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [videoError, setVideoError] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    let active = true;

    const loadProduct = async () => {
      setLoading(true);
      setProduct(null);
      setRelatedProducts([]);
      setActiveImage(0);
      setQuantity(1);
      setVideoError(false);
      setVideoMuted(true);

      const numericId = Number(slug);
      const productQuery = supabase
        .from('products_1750000001')
        .select('*');

      const { data, error } = await (
        Number.isInteger(numericId) && numericId > 0
          ? productQuery.eq('id', numericId)
          : productQuery.eq('slug', slug)
      ).maybeSingle();

      if (!active) return;

      if (error || !data) {
        setLoading(false);
        return;
      }

      setProduct(data);

      const { data: related } = await supabase
        .from('products_1750000001')
        .select('*')
        .eq('category', data.category)
        .neq('id', data.id)
        .limit(4);

      if (!active) return;
      setRelatedProducts(related || []);
      setLoading(false);
    };

    loadProduct();

    return () => {
      active = false;
    };
  }, [slug]);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setVideoMuted(videoRef.current.muted);
    }
  };

  if (loading) {
    return (
      <main className="product-detail-page">
        <div className="section-shell" style={{ padding: '120px 0', textAlign: 'center' }}>
          <p>Loading product…</p>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="product-detail-page">
        <div className="section-shell" style={{ padding: '120px 0', textAlign: 'center' }}>
          <h2>Product not found</h2>
          <Link to="/shop" className="button primary" style={{ marginTop: 20 }}>Back to Shop</Link>
        </div>
      </main>
    );
  }

  const images = [product.image, product.image_2, product.image_3, product.image_4, product.image_5].filter(Boolean);
  const hasVideo = Boolean(product.video_url);
  const totalSlides = images.length + (hasVideo ? 1 : 0);
  const isVideoSlide = hasVideo && activeImage === images.length;
  const prevImage = () => setActiveImage((i) => (i - 1 + totalSlides) % totalSlides);
  const nextImage = () => setActiveImage((i) => (i + 1) % totalSlides);

  const renderVideoPlayer = () => {
    const url = product.video_url;
    const directVideo = isDirectVideoUrl(url);

    if (videoError) {
      return (
        <div className="gallery-video-error">
          <SafeIcon icon={FiAlertCircle} />
          <p>Unable to load video</p>
          <a href={url} target="_blank" rel="noopener noreferrer" className="button secondary">
            Open in new tab
          </a>
        </div>
      );
    }

    if (directVideo) {
      return (
        <div className="gallery-video">
          <video
            key={url}
            ref={videoRef}
            src={url}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            referrerPolicy="no-referrer"
            preload="metadata"
            onError={() => setVideoError(true)}
          >
            Your browser does not support the video tag.
          </video>
          <button
            className="video-mute-toggle"
            onClick={toggleMute}
            aria-label={videoMuted ? 'Unmute video' : 'Mute video'}
            type="button"
          >
            <SafeIcon icon={videoMuted ? FiVolumeX : FiVolume2} />
          </button>
        </div>
      );
    }

    return (
      <div className="gallery-video">
        <iframe
          key={url}
          src={url}
          title="Product video"
          allow="autoplay"
          allowFullScreen={false}
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          onError={() => setVideoError(true)}
        />
      </div>
    );
  };

  return (
    <main className="product-detail-page">
      <div className="product-detail-breadcrumb section-shell">
        <Link to="/shop"><SafeIcon icon={FiArrowLeft} /> Back to Shop</Link>
        <span>{product.category}</span>
      </div>

      <section className="product-detail-main section-shell">
        <div className="product-detail-gallery">
          <div className="gallery-thumbs">
            {images.map((img, i) => (
              <button
                key={i}
                className={`gallery-thumb ${i === activeImage ? 'active' : ''}`}
                onClick={() => setActiveImage(i)}
              >
                <img src={img} alt={`${product.name} ${i + 1}`} />
              </button>
            ))}
            {hasVideo && (
              <button
                key="video-thumb"
                className={`gallery-thumb gallery-thumb-video ${isVideoSlide ? 'active' : ''}`}
                onClick={() => { setActiveImage(images.length); setVideoError(false); }}
              >
                <div className="thumb-video-icon">
                  <SafeIcon icon={FiPlay} />
                </div>
              </button>
            )}
          </div>
          <div className="gallery-main">
            <button className="gallery-nav gallery-prev" onClick={prevImage}><SafeIcon icon={FiChevronLeft} /></button>
            {isVideoSlide ? renderVideoPlayer() : (
              <img src={images[activeImage] || product.image} alt={product.name} />
            )}
            <button className="gallery-nav gallery-next" onClick={nextImage}><SafeIcon icon={FiChevronRight} /></button>
          </div>
        </div>

        <div className="product-detail-info">
          <span className="product-detail-category">{product.category}</span>
          <h1>{product.name}</h1>
          <div className="product-detail-price">
            <span className="price-current">Rs. {Number(product.price).toLocaleString()}</span>
            <span className="price-weight">{product.weight}</span>
          </div>
          {product.stock_quantity != null && (
            <span className="product-stock-count">{Math.max(0, Number(product.stock_quantity))} left in stock</span>
          )}

          <div className="product-detail-rating">
            <div className="stars">
              {Array.from({ length: 5 }, (_, i) => <SafeIcon icon={FiStar} key={i} />)}
            </div>
            <span>(24 reviews)</span>
          </div>

          <p className="product-detail-description">{product.description}</p>

          <div className="product-detail-quantity">
            <label>Quantity</label>
            <div className="quantity-control">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)}>+</button>
            </div>
          </div>

          <div className="product-detail-actions">
            <button
              className="button primary gradient-btn"
              disabled={!product.available}
              onClick={() => {
                for (let i = 0; i < quantity; i++) onAdd(product);
              }}
            >
              <SafeIcon icon={FiShoppingBag} />
              {product.available ? 'Add to Cart' : 'Out of Stock'}
            </button>
          </div>

          <div className="product-detail-perks">
            <div className="perk"><SafeIcon icon={FiTruck} /><span>Island-wide Delivery</span></div>
            <div className="perk"><SafeIcon icon={FiShield} /><span>100% Natural</span></div>
            <div className="perk"><SafeIcon icon={FiRefreshCw} /><span>Easy Returns</span></div>
          </div>
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="related-products section-shell">
          <Reveal className="section-heading">
            <span className="eyebrow">You may also like</span>
            <h2>Related Products</h2>
            <div className="section-divider" />
          </Reveal>
          <div className="product-grid">
            {relatedProducts.map((p) => (
              <Reveal key={p.id} delay={0.05}>
                <ProductCard product={p} onAdd={onAdd} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

export default ProductDetail;